import { useState, useEffect, useRef, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import type { LibraryNode } from "../../../lib/types";
import { useCreatePrompt } from "../../hooks/use-prompt-mutations";
import { showToast } from "../primitives";

interface NewPromptModalProps {
  open: boolean;
  onClose: () => void;
  /** Scope the prompt is created under (an agent or folder; root for global). */
  parentId?: string;
  parentName?: string;
  onCreated?: (node: LibraryNode) => void;
}

const DESCRIPTION_LIMIT = 200;

/** Lower-case, hyphenate, strip noise → a stable prompt slug. */
function toSlug(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Sprint 11 (S11.48) — create a Prompt node. Mirrors NewFolderModal. Creates an
 * empty-body, empty-args prompt and opens the editor on success so the author
 * lands straight in the args + template surface.
 */
export function NewPromptModal({ open, onClose, parentId, parentName, onCreated }: NewPromptModalProps) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const nameRef = useRef<HTMLInputElement>(null);
  const create = useCreatePrompt();

  useEffect(() => {
    if (open) {
      setName("");
      setDescription("");
      setTimeout(() => nameRef.current?.focus(), 50);
    }
  }, [open]);

  if (!open) return null;

  const slug = toSlug(name);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!slug) return;
    create.mutate(
      {
        name: slug,
        description: description.trim() || undefined,
        parent_id: parentId || undefined,
        body: "",
        arguments: [],
      },
      {
        onSuccess: (node) => {
          showToast(`Prompt '${node.name}' created`);
          onCreated?.(node);
          onClose();
          navigate(`/library/${node.id}`);
        },
        onError: (err) => {
          const msg = err instanceof Error ? err.message : "Something went wrong";
          showToast(`Couldn't create prompt: ${msg}`, "error", true);
        },
      },
    );
  }

  return (
    <div style={overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modal} role="dialog" aria-label="New prompt">
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <div style={iconBox}>
            <i className="ti ti-bolt" aria-hidden="true" />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 500, color: "var(--color-text-primary)" }}>
              New prompt
            </div>
            <div style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>
              {parentName ? `An invocable template scoped to ${parentName}` : "An invocable template — composes workspace state into host guidance"}
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={field}>
            <label style={label}>Name</label>
            <input
              ref={nameRef}
              type="text"
              style={input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Handle enquiry"
              maxLength={80}
            />
            {slug && slug !== name ? (
              <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 4 }}>
                Saved as <code>{slug}</code>
              </div>
            ) : null}
          </div>

          <div style={field}>
            <label style={label}>Description</label>
            <input
              type="text"
              style={input}
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, DESCRIPTION_LIMIT))}
              placeholder="What invoking it produces. Optional — 200 chars max."
              maxLength={DESCRIPTION_LIMIT}
            />
          </div>

          <div style={hintBox}>
            Prompts are invocable templates (the DOES primitive). After creating, declare typed
            arguments and write the body using <code>{"{{arg}}"}</code>, <code>{"{{?tool}}"}</code>, and{" "}
            <code>{"[[ref]]"}</code> — then test-compose it.
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
            <button type="button" onClick={onClose} style={{ fontSize: 13, padding: "8px 14px" }}>
              Cancel
            </button>
            <button
              type="submit"
              data-variant="primary"
              disabled={!slug || create.isPending}
              style={{ fontSize: 13, padding: "8px 14px" }}
            >
              {create.isPending ? "Creating…" : "Create prompt"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const overlay: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.3)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 100,
  fontFamily: "var(--font-sans)",
};
const modal: CSSProperties = {
  background: "var(--color-background-primary)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-lg)",
  padding: 24,
  width: 440,
  maxWidth: "90vw",
};
const field: CSSProperties = { marginBottom: 16 };
const label: CSSProperties = {
  display: "block",
  fontSize: 12,
  fontWeight: 500,
  color: "var(--color-text-secondary)",
  marginBottom: 4,
};
const input: CSSProperties = {
  width: "100%",
  fontSize: 13,
  padding: "8px 10px",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
  boxSizing: "border-box",
};
const iconBox: CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: "var(--border-radius-md)",
  background: "var(--color-background-secondary)",
  border: "var(--kuvio-border-default)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "var(--color-text-secondary)",
  fontSize: 20,
  flexShrink: 0,
};
const hintBox: CSSProperties = {
  fontSize: 12,
  fontStyle: "italic",
  color: "var(--color-text-tertiary)",
  lineHeight: 1.5,
  marginTop: 4,
  paddingTop: 12,
  borderTop: "var(--kuvio-border-default)",
};
